import { z } from 'zod';
import { LicenseTypes } from '../../../types/licenses';
import { BasicBusinessInformationSchema } from './types/businessInformation';

export const PODCAST_CONTENT_PATH = 'licenses.steps.businessInformation.forms.podcast';

// Helper regex validators reused for published/unpublished fields
const numeric7 = /^\d{1,7}$/;
const numeric9Currency = /^\d{1,9}(\s?(kr\.?|DKK))?$/i;
// Strict HH:MM where HH:MM is 00:00–23:59 (24:00 not allowed)
const timeFormat = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

// Base field builders without unconditional "required" so we can apply conditional requirement in superRefine
// Title: apply trim then length constraint; we can't chain .max AFTER transform without keeping effects, so perform length check manually in superRefine.
const titleField = z.string().transform(v => v.trim());

const dateField = z.string().refine(v => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), {
  message: `${PODCAST_CONTENT_PATH}.firstEpisodeDate.validation.required`,
});

const streamsRangeField = z.enum(['0-500', '501-5000', '5000+', 'unknown', 'none']);

const averageStreamsField = z.string().refine(v => !v || numeric7.test(v.replace(/\./g, '')), {
  message: `${PODCAST_CONTENT_PATH}.streamsSection.averageStreamsPerMonth.validation.numeric`,
});

const expectedRevenueField = z.string().refine(v => !v || numeric9Currency.test(v.replace(/\./g, '')), {
  message: `${PODCAST_CONTENT_PATH}.streamsSection.expectedRevenuePerMonth.validation.numeric`,
});

const averageRevenueField = z.string().refine(v => !v || numeric9Currency.test(v.replace(/\./g, '')), {
  message: `${PODCAST_CONTENT_PATH}.streamsSection.averageRevenuePerMonth.validation.numeric`,
});

const episodeDurationField = z.string().refine(v => !v || timeFormat.test(v), {
  message: `${PODCAST_CONTENT_PATH}.contentSection.averageEpisodeDuration.validation.format`,
});

const musicMinutesField = z.string().refine(v => !v || timeFormat.test(v), {
  message: `${PODCAST_CONTENT_PATH}.contentSection.averageMusicMinutesPerEpisode.validation.format`,
});

export const podcastSchema = BasicBusinessInformationSchema.extend({
  title: titleField,
  isPublished: z.boolean().nullable(),
  firstEpisodeDate: dateField,
  expectedStreamsRange: streamsRangeField,
  averageStreamsPerMonth: averageStreamsField,
  expectedRevenuePerMonth: expectedRevenueField,
  averageRevenuePerMonth: averageRevenueField,
  averageEpisodeDuration: episodeDurationField,
  averageMusicMinutesPerEpisode: musicMinutesField,
}).superRefine((data, ctx) => {
  // isPublished must be chosen
  if (data.isPublished === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${PODCAST_CONTENT_PATH}.published.validation.required`,
      path: ['isPublished'],
    });
  }

  // Fields required when podcast IS published
  if (data.isPublished === true) {
    if (!data.firstEpisodeDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.firstEpisodeDate.validation.required`,
        path: ['firstEpisodeDate'],
      });
    }
    // averageStreamsPerMonth & averageRevenuePerMonth are required when published
    if (!data.averageStreamsPerMonth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.streamsSection.averageStreamsPerMonth.validation.required`,
        path: ['averageStreamsPerMonth'],
      });
    }
    if (!data.averageRevenuePerMonth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.streamsSection.averageRevenuePerMonth.validation.required`,
        path: ['averageRevenuePerMonth'],
      });
    }
    if (!data.averageEpisodeDuration) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.contentSection.averageEpisodeDuration.validation.required`,
        path: ['averageEpisodeDuration'],
      });
    }
    if (!data.averageMusicMinutesPerEpisode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.contentSection.averageMusicMinutesPerEpisode.validation.required`,
        path: ['averageMusicMinutesPerEpisode'],
      });
    }
  }

  // Fields required when podcast is NOT published
  if (data.isPublished === false) {
    if (data.expectedStreamsRange === 'none') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${PODCAST_CONTENT_PATH}.expectedStreams.validation.required`,
        path: ['expectedStreamsRange'],
      });
    }
  }

  // Shared required fields (always visible regardless of published state)
  if (!data.title) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${PODCAST_CONTENT_PATH}.title.validation.required`,
      path: ['title'],
    });
  }
  if (data.title && data.title.length > 200) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${PODCAST_CONTENT_PATH}.title.validation.maxLength`,
      path: ['title'],
    });
  }
});

export const createPodcastDefaults = (): z.infer<typeof podcastSchema> => ({
  licenseCode: LicenseTypes.Podcast,
  title: '',
  isPublished: false,
  firstEpisodeDate: '',
  expectedStreamsRange: 'none',
  averageStreamsPerMonth: '',
  expectedRevenuePerMonth: '',
  averageRevenuePerMonth: '',
  averageEpisodeDuration: '',
  averageMusicMinutesPerEpisode: '',
});

export type PodcastForm = z.infer<typeof podcastSchema>;
